import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { labelTextColor } from "@/lib/labels";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Plus, ExternalLink, Pencil, Trash2, Settings2, CalendarClock, Users2, ChevronLeft, ChevronRight,
  ChevronDown, User, Camera, GripVertical, MessageSquare, FolderPlus, RefreshCw, Upload, FileDown, Archive, ArchiveRestore,
} from "lucide-react";
import { CategoryTemplateMenu } from "@/components/CategoryTemplateMenu";
import { useAuth } from "@/context/AuthContext";

function LabelChip({ def, active, onClick }) {
  const bg = def.color || "#3B82F6";
  const style = active
    ? { backgroundColor: bg, color: def.text_color || labelTextColor(bg), borderColor: def.border_color || bg }
    : {};
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={style}
      className={`w-full text-center text-[10px] leading-tight font-bold px-1.5 py-1 rounded-md border break-words transition-colors ${
        active ? "shadow-sm" : "bg-white text-slate-400 border-slate-200 border-dashed hover:border-slate-400 hover:text-slate-600"
      }`}
      title={`${def.name} – ${active ? "Klicken zum Entfernen" : "Klicken zum Setzen"}`}
      data-testid={`label-chip-${def.id}`}
    >
      {def.name.toUpperCase()}
    </button>
  );
}

function PersonPicker({ role, label, name, email, onChange, testKey }) {
  const tk = testKey || role;
  const [opts, setOpts] = useState([]);
  const [sel, setSel] = useState("");
  useEffect(() => { api.get(`/people/selectable?role=${role}`).then((r) => setOpts(r.data)).catch(() => {}); }, [role]);
  useEffect(() => {
    const match = opts.find((o) => o.name === name && (o.email || "") === (email || ""));
    setSel(match ? match.id : (name ? "__free__" : ""));
  }, [opts, name, email]);
  const pick = (val) => {
    setSel(val);
    if (val === "__free__") { onChange({ name: name || "", email: "" }); return; }
    const o = opts.find((x) => x.id === val);
    if (o) onChange({ name: o.name, email: o.email || "" });
  };
  return (
    <div>
      <Label>{label}</Label>
      <Select value={sel} onValueChange={pick}>
        <SelectTrigger className="mt-1" data-testid={`article-${tk}-select`}><SelectValue placeholder="Aus Liste wählen…" /></SelectTrigger>
        <SelectContent>
          {opts.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}{o.email ? ` · ${o.email}` : ""}</SelectItem>)}
          <SelectItem value="__free__">— Freier Name —</SelectItem>
        </SelectContent>
      </Select>
      {sel === "__free__" && (
        <Input value={name} onChange={(e) => onChange({ name: e.target.value, email: "" })} className="mt-2" placeholder="Name eingeben" data-testid={`article-${tk}-free`} />
      )}
    </div>
  );
}

function ArticleCard({ article, labelDefs, onEdit, onDelete, onDragStart, onToggleLabel, onDragOver, onDragEnd, canArt, canStatus }) {
  const active = article.labels || [];
  return (
    <div
      draggable={canArt}
      onDragStart={(e) => { if (canArt) onDragStart(e, article); }}
      onDragOver={(e) => onDragOver?.(e, article)}
      onDragEnd={onDragEnd}
      onClick={() => onEdit(article)}
      className="article-card bg-white border border-slate-200 rounded-lg p-3 shadow-sm cursor-pointer hover:border-slate-300 hover:shadow-md transition"
      title="Zum Bearbeiten klicken"
      data-testid={`article-card-${article.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-1.5 min-w-0">
          <GripVertical size={14} className="text-slate-300 mt-0.5 shrink-0 cursor-grab" />
          <div className="min-w-0">
            {article.number && <span className="mono text-xs text-slate-400 mr-1">{article.number}</span>}
            <span className="text-sm font-semibold text-slate-900 leading-snug">{article.title}</span>
          </div>
        </div>
      </div>

      {/* Direkt anklickbare Status-Labels */}
      <div className="grid grid-cols-2 gap-1 mt-2.5" data-testid={`article-labels-${article.id}`}>
        {labelDefs.map((def) => (
          <LabelChip key={def.id} def={def} active={active.includes(def.id)} onClick={() => { if (canStatus) onToggleLabel(article, def.id); else toast.error("Keine Berechtigung für den Artikel-Status"); }} />
        ))}
        {labelDefs.length === 0 && <span className="col-span-2 text-[11px] text-slate-400 italic">Keine Labels definiert</span>}
      </div>

      {(article.author || article.author2 || article.photographer) && (
        <div className="mt-2.5 space-y-0.5 text-xs text-slate-500">
          {(article.author || article.author2) && <div className="flex items-center gap-1.5" data-testid={`article-authors-${article.id}`}><User size={12} className="shrink-0" /> {[article.author, article.author2].filter(Boolean).join(" & ")}</div>}
          {article.photographer && <div className="flex items-center gap-1.5"><Camera size={12} /> {article.photographer}</div>}
        </div>
      )}

      <div className="mt-3 flex items-center flex-wrap gap-2">
        {article.drive_link ? (
          <a href={article.drive_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2.5 py-1.5"
            data-testid={`article-drive-${article.id}`}>
            <ExternalLink size={12} /> Ordner öffnen
          </a>
        ) : canArt ? (
          <button onClick={(e) => { e.stopPropagation(); onEdit(article); }} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-slate-600 border border-dashed border-slate-300 rounded-md px-2.5 py-1.5">
            <ExternalLink size={12} /> Drive-Link
          </button>
        ) : null}
        {article.open_comments > 0 && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1"
            title="Offene Kommentare" data-testid={`article-comments-badge-${article.id}`}>
            <MessageSquare size={12} /> {article.open_comments}
          </span>
        )}
      </div>
    </div>
  );
}

const emptyArticle = { title: "", number: "", author: "", author_email: "", author2: "", author2_email: "", photographer: "", photographer_email: "", drive_link: "" };

export default function Board() {
  const { id } = useParams();
  const [board, setBoard] = useState(null);
  const [labelDefs, setLabelDefs] = useState([]);
  const [dragOverCat, setDragOverCat] = useState(null);
  const [artOpen, setArtOpen] = useState(false);
  const [artSaving, setArtSaving] = useState(false);
  const [folderBusy, setFolderBusy] = useState(false);
  const [artForm, setArtForm] = useState(emptyArticle);
  const [artEditId, setArtEditId] = useState(null);
  const [artCat, setArtCat] = useState(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueForm, setIssueForm] = useState({});
  const [catName, setCatName] = useState("");
  const [catOpen, setCatOpen] = useState(false);
  const [drive, setDrive] = useState({ connected: false, configured: false, provider: "" });
  const [comments, setComments] = useState({ count: 0, open: 0, comments: [] });
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [drag, setDrag] = useState(null);
  const [hint, setHint] = useState(null);
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || (user?.permissions?.artikel_loeschen === "edit" && user?.permissions?.artikel_bearbeiten === "edit");
  const canArt = user?.role === "admin" || user?.permissions?.artikel_bearbeiten === "edit";
  const canStatus = user?.role === "admin" || user?.permissions?.artikel_status === "edit";
  const canMoveCat = user?.permissions?.kategorien_verschieben === "edit";
  const canDrive = user?.role === "admin" || (user?.permissions?.drive_zugriff === "edit" && canArt);

  const load = useCallback(async () => {
    try {
      const [b, l, d] = await Promise.all([api.get(`/issues/${id}/board`), api.get("/status-labels"), api.get("/storage/status")]);
      setBoard(b.data);
      setLabelDefs(l.data);
      setDrive(d.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!board) return <div className="p-8 text-slate-400">Lädt…</div>;
  const { issue, categories, articles } = board;
  const byCat = (catId) => articles.filter((a) => a.category_id === catId).sort((a, b) => a.order - b.order);

  // ---- labels (optimistisch) ----
  const toggleLabel = async (article, labelId) => {
    const current = article.labels || [];
    const next = current.includes(labelId) ? current.filter((x) => x !== labelId) : [...current, labelId];
    setBoard((prev) => ({
      ...prev,
      articles: prev.articles.map((a) => (a.id === article.id ? { ...a, labels: next } : a)),
    }));
    try {
      await api.put(`/articles/${article.id}/toggle-label`, { label_id: labelId });
    } catch (e) {
      toast.error("Konnte Label nicht speichern");
      load();
    }
  };

  // ---- article ----
  const openNewArticle = (categoryId) => { setArtForm(emptyArticle); setArtEditId(null); setArtCat(categoryId); setArtOpen(true); };
  const openEditArticle = (a) => { setArtForm({ ...emptyArticle, ...a }); setArtEditId(a.id); setArtCat(a.category_id); setComments({ count: 0, open: 0, comments: [] }); setNewComment(""); setArtOpen(true); loadComments(a.id); };

  const loadComments = async (aid) => {
    setCommentsLoading(true);
    try { const { data } = await api.get(`/articles/${aid}/comments`); setComments(data); }
    catch { /* ignore */ }
    finally { setCommentsLoading(false); }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/articles/${artEditId}/comments`, { content: newComment.trim() });
      setNewComment("");
      loadComments(artEditId);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resolveComment = async (c) => {
    try { await api.put(`/articles/${artEditId}/comments/${c.id}/resolve`, { resolved: !c.resolved }); loadComments(artEditId); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const deleteComment = async (c) => {
    try { await api.delete(`/articles/${artEditId}/comments/${c.id}`); loadComments(artEditId); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const ensureFolder = async () => {
    if (folderBusy) return;
    setFolderBusy(true);
    try {
      const { data } = await api.post(`/storage/article/${artEditId}/folder`);
      toast.success("Google-Ordner erstellt & verknüpft");
      setArtForm((f) => ({ ...f, drive_link: data.web_view_link, upload_link: data.upload_link }));
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setFolderBusy(false); }
  };

  const saveArticle = async () => {
    if (artSaving) return;
    if (!artForm.title.trim()) { toast.error("Titel erforderlich"); return; }
    const payload = { title: artForm.title, number: artForm.number, author: artForm.author,
      author_email: artForm.author_email, author2: artForm.author2, author2_email: artForm.author2_email,
      photographer: artForm.photographer,
      photographer_email: artForm.photographer_email, drive_link: artForm.drive_link };
    setArtSaving(true);
    try {
      let created = null;
      if (artEditId) {
        await api.put(`/articles/${artEditId}`, payload);
      } else {
        ({ data: created } = await api.post("/articles", { issue_id: id, category_id: artCat, ...payload }));
      }
      toast.success("Gespeichert");
      setArtOpen(false);
      load();
      if (created?.id && drive.connected && drive.auto_folder !== false && canDrive) {
        const t = toast.loading("Google-Ordner wird erstellt …");
        api.post(`/storage/article/${created.id}/folder`)
          .then(() => { toast.success("Google-Ordner erstellt & verknüpft", { id: t }); load(); })
          .catch((e) => toast.error("Artikel gespeichert – Ordner konnte nicht erstellt werden: " + apiError(e.response?.data?.detail), { id: t }));
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setArtSaving(false); }
  };

  const removeArticle = async (a) => {
    if (!window.confirm(`Artikel "${a.title}" löschen?`)) return false;
    const hasFolder = !!a.drive_link && drive.connected && canDrive;
    const trash = hasFolder && window.confirm("Auch den zugehörigen Google-Ordner in den Papierkorb von Google Drive verschieben?\n\nOK = Ordner mitlöschen · Abbrechen = Ordner behalten");
    try {
      const { data } = await api.delete(`/articles/${a.id}`, { params: { trash_folder: trash } });
      toast.success(trash ? (data.folder_trashed ? "Artikel gelöscht, Ordner im Drive-Papierkorb" : "Artikel gelöscht – Ordner konnte nicht verschoben werden") : "Artikel gelöscht");
      load();
      return true;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); return false; }
  };

  const deleteArticle = (a) => removeArticle(a);

  const deleteArticleFromDialog = async () => {
    if (!artEditId) return;
    if (await removeArticle({ ...artForm, id: artEditId })) setArtOpen(false);
  };

  // ---- drag & drop (Trello-artig) ----
  const onDragStart = (e, article) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", article.id);
    setDrag({ type: "article", id: article.id });
  };
  const onCardDragOver = (e, article) => {
    if (drag?.type !== "article") return;
    e.preventDefault(); e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    const list = byCat(article.category_id);
    const idx = list.findIndex((a) => a.id === article.id) + (e.clientY > r.top + r.height / 2 ? 1 : 0);
    if (hint?.catId !== article.category_id || hint?.index !== idx) setHint({ catId: article.category_id, index: idx });
  };
  const endDrag = () => { setDrag(null); setHint(null); setDragOverCat(null); };
  const onDrop = async (e, categoryId) => {
    e.preventDefault();
    const d = drag;
    const h = hint;
    endDrag();
    if (!d) return;
    if (d.type === "category") {
      const ids = categories.map((c) => c.id).filter((x) => x !== d.id);
      const target = categories.findIndex((c) => c.id === categoryId);
      const from = categories.findIndex((c) => c.id === d.id);
      if (from === target) return;
      ids.splice(target, 0, d.id);
      setBoard((b) => ({ ...b, categories: ids.map((cid, i) => ({ ...b.categories.find((c) => c.id === cid), order: i })) }));
      await api.put("/categories/reorder", { issue_id: id, ids }).catch((err) => toast.error(apiError(err.response?.data?.detail)));
      load();
      return;
    }
    const list = byCat(categoryId).map((a) => a.id);
    let index = h?.catId === categoryId ? h.index : list.length;
    const oldIdx = list.indexOf(d.id);
    if (oldIdx >= 0) { list.splice(oldIdx, 1); if (oldIdx < index) index -= 1; }
    list.splice(index, 0, d.id);
    setBoard((b) => ({ ...b, articles: b.articles.map((a) => (list.includes(a.id) ? { ...a, category_id: categoryId, order: list.indexOf(a.id) } : a)) }));
    await api.put("/articles-order", { category_id: categoryId, ids: list }).catch((err) => toast.error(apiError(err.response?.data?.detail)));
    load();
  };

  // ---- category ----
  const addCategory = async () => {
    if (!catName.trim()) return;
    await api.post("/categories", { issue_id: id, name: catName });
    setCatName(""); setCatOpen(false); load();
  };
  const deleteCategory = async (c) => {
    if (!window.confirm(`Kategorie "${c.name}" inkl. Artikel löschen?`)) return;
    await api.delete(`/categories/${c.id}`);
    load();
  };
  const renameCategory = async (c) => {
    const name = window.prompt("Kategorie umbenennen:", c.name);
    if (!name?.trim() || name === c.name) return;
    await api.put(`/categories/${c.id}`, { name: name.trim() });
    load();
  };

  // ---- issue settings ----
  const openIssueSettings = () => { setIssueForm({ ...issue, schedule: issue.schedule || {} }); setIssueOpen(true); };
  const saveIssue = async () => {
    await api.put(`/issues/${id}`, issueForm);
    toast.success("Ausgabe aktualisiert");
    setIssueOpen(false);
    load();
  };
  const toggleArchive = async () => {
    const toArchive = issue.status !== "archiv";
    if (toArchive && !window.confirm(`„${issue.name}“ ins Archiv verschieben?`)) return;
    await api.put(`/issues/${id}`, { status: toArchive ? "archiv" : "aktiv" });
    toast.success(toArchive ? "Ausgabe archiviert" : "Ausgabe wiederhergestellt");
    setIssueOpen(false);
    load();
  };
  const downloadChecklist = async () => {
    try {
      const r = await api.get(`/issues/${id}/checklist.pdf`, { responseType: "blob", params: { t: Date.now() } });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Checkliste_${issue.name || "Ausgabe"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Checkliste konnte nicht erstellt werden"); }
  };
  const fmtDate = (s) => {
    if (!s) return "";
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleDateString("de-DE");
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="px-6 lg:px-8 pt-6 pb-4 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display text-2xl lg:text-3xl font-bold text-slate-900" data-testid="board-issue-title">{issue.name}</h1>
          <div className="flex items-center gap-2 flex-wrap">
            {issue.redaktionsschluss && (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg px-3 py-1.5">
                <CalendarClock size={15} /> Redaktionsschluss: {fmtDate(issue.redaktionsschluss)}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={openIssueSettings} data-testid="issue-settings-button">
              <Settings2 size={15} className="mr-1.5" /> Einstellungen
            </Button>
            <CategoryTemplateMenu issueId={id} onApplied={load} />
            <Button size="sm" onClick={() => setCatOpen(true)} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="add-category-button">
              <Plus size={15} className="mr-1.5" /> Kategorie
            </Button>
          </div>
        </div>
      </div>

      {/* Board */}
      <div className="flex-1 overflow-x-auto board-scroll p-6 lg:p-8">
        <div className="flex gap-5 items-start">
          {/* Magazin info */}
          <div className="w-72 shrink-0 bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="magazin-info-widget">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Magazin-Info</div>
            <Collapsible defaultOpen>
              <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-semibold text-slate-800 py-2">
                <span className="flex items-center gap-2"><Users2 size={15} /> Redaktionsteam</span>
                <ChevronDown size={15} />
              </CollapsibleTrigger>
              <CollapsibleContent className="text-sm text-slate-600 space-y-1 pb-2">
                {issue.team_leitung && <div><span className="text-slate-400">Leitung:</span> {issue.team_leitung}</div>}
                {issue.team_redaktion && <div><span className="text-slate-400">Redaktion:</span> {issue.team_redaktion}</div>}
                {issue.team_layout && <div><span className="text-slate-400">Layout:</span> {issue.team_layout}</div>}
                {issue.kontakt && <div><span className="text-slate-400">Kontakt:</span> {issue.kontakt}</div>}
                {!issue.team_leitung && !issue.team_redaktion && <div className="text-slate-400 italic">Noch nicht gepflegt</div>}
              </CollapsibleContent>
            </Collapsible>
            <div className="h-px bg-slate-100 my-1" />
            <Collapsible defaultOpen>
              <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-semibold text-slate-800 py-2">
                <span className="flex items-center gap-2"><CalendarClock size={15} /> Zeitplan</span>
                <ChevronDown size={15} />
              </CollapsibleTrigger>
              <CollapsibleContent className="text-sm pb-2">
                {(issue.computed_schedule || []).map((row, i) => (
                  <div key={i} className="flex justify-between py-1 border-b border-slate-50 last:border-0">
                    <span className="text-slate-500">{row.name}</span>
                    <span className="font-medium text-slate-700">{fmtDate(row.date)}</span>
                  </div>
                ))}
                {(!issue.computed_schedule || issue.computed_schedule.length === 0) && (
                  <div className="text-slate-400 italic">Redaktionsschluss in den Einstellungen der Ausgabe eintragen</div>
                )}
              </CollapsibleContent>
            </Collapsible>
            <div className="h-px bg-slate-100 my-2" />
            <Button variant="outline" size="sm" onClick={downloadChecklist} className="w-full mt-1" data-testid="checklist-pdf-button">
              <FileDown size={15} className="mr-1.5" /> Checkliste als PDF
            </Button>
          </div>

          {/* Columns */}
          {categories.map((cat) => {
            const list = byCat(cat.id);
            return (
              <div key={cat.id}
                onDragOver={(e) => {
                  e.preventDefault(); setDragOverCat(cat.id);
                  if (drag?.type === "article" && e.target === e.currentTarget) setHint({ catId: cat.id, index: list.length });
                }}
                onDrop={(e) => onDrop(e, cat.id)}
                className={`w-80 shrink-0 bg-slate-100/80 border border-slate-200/80 rounded-xl p-3 flex flex-col transition-opacity ${dragOverCat === cat.id ? "card-drag-over" : ""} ${drag?.type === "category" && drag.id === cat.id ? "opacity-40" : ""}`}
                data-testid={`category-column-${cat.id}`}>
                <div className={`flex items-center justify-between px-1 mb-3 ${canMoveCat ? "cursor-grab active:cursor-grabbing" : ""}`} draggable={canMoveCat}
                  onDragStart={(e) => { if (!canMoveCat) return; e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", cat.id); setDrag({ type: "category", id: cat.id }); }}
                  onDragEnd={endDrag} title={canMoveCat ? "Kategorie per Maus verschieben" : ""} data-testid={`category-drag-${cat.id}`}>
                  <div className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                    {canMoveCat && <GripVertical size={14} className="text-slate-400" />}{cat.name} <span className="text-slate-400 font-medium">{list.length}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => renameCategory(cat)} title="Umbenennen" className="text-slate-400 hover:text-slate-700" data-testid={`category-rename-${cat.id}`}><Pencil size={13} /></button>
                    {canArt && <button onClick={() => openNewArticle(cat.id)} className="text-slate-400 hover:text-slate-700" data-testid={`category-add-article-${cat.id}`}>
                      <Plus size={16} />
                    </button>}
                    <button onClick={() => deleteCategory(cat)} className="text-slate-400 hover:text-red-500" data-testid={`category-delete-${cat.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="space-y-2.5 min-h-[40px]" onDragOver={(e) => { if (drag?.type === "article" && e.target === e.currentTarget) { e.preventDefault(); setHint({ catId: cat.id, index: list.length }); } }}>
                  {list.map((a, i) => (
                    <div key={a.id}>
                      {hint?.catId === cat.id && hint.index === i && drag?.id !== a.id && <div className="h-1.5 rounded-full bg-blue-500 mb-2.5" data-testid="drop-indicator" />}
                      <div className={drag?.id === a.id ? "opacity-40" : ""}>
                        <ArticleCard article={a} labelDefs={labelDefs} onEdit={openEditArticle} onDragOver={onCardDragOver} onDragEnd={endDrag}
                          onDelete={deleteArticle} onDragStart={onDragStart} onToggleLabel={toggleLabel} canArt={canArt} canStatus={canStatus} />
                      </div>
                    </div>
                  ))}
                  {hint?.catId === cat.id && hint.index === list.length && <div className="h-1.5 rounded-full bg-blue-500" data-testid="drop-indicator" />}
                </div>
                {canArt && <button onClick={() => openNewArticle(cat.id)}
                  className="mt-2.5 w-full text-sm text-slate-500 hover:text-slate-800 hover:bg-white/70 rounded-lg py-2 flex items-center justify-center gap-1.5 transition"
                  data-testid={`category-footer-add-${cat.id}`}>
                  <Plus size={15} /> Artikel hinzufügen
                </button>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Article dialog */}
      <Dialog open={artOpen} onOpenChange={setArtOpen}>
        <DialogContent className="max-w-lg" data-testid="article-dialog">
          <DialogHeader><DialogTitle>{artEditId ? "Artikel bearbeiten" : "Neuer Artikel"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-3">
              <Label>Titel *</Label>
              <Input value={artForm.title} onChange={(e) => setArtForm({ ...artForm, title: e.target.value })} className="mt-1" data-testid="article-title-input" />
              <p className="text-xs text-slate-400 mt-1">Die Artikelnummer (z.&nbsp;B. 0101) wird automatisch aus Kapitel + Position vergeben.</p>
            </div>
            <div className="col-span-3 sm:col-span-1">
              <PersonPicker role="author" label="Autor" name={artForm.author} email={artForm.author_email}
                onChange={({ name, email }) => setArtForm((f) => ({ ...f, author: name, author_email: email }))} />
            </div>
            <div className="col-span-3 sm:col-span-1">
              <PersonPicker role="author" testKey="author2" label="2. Autor (optional)" name={artForm.author2} email={artForm.author2_email}
                onChange={({ name, email }) => setArtForm((f) => ({ ...f, author2: name, author2_email: email }))} />
            </div>
            <div className="col-span-3 sm:col-span-1">
              <PersonPicker role="photographer" label="Fotograf" name={artForm.photographer} email={artForm.photographer_email}
                onChange={({ name, email }) => setArtForm((f) => ({ ...f, photographer: name, photographer_email: email }))} />
            </div>
            {artForm.drive_link && (
              <div className="col-span-3">
                <Label>Google Drive Ordner</Label>
                <a href={artForm.drive_link} target="_blank" rel="noreferrer" className="mt-1 flex items-center gap-1.5 text-sm text-emerald-700 hover:underline break-all" data-testid="article-drive-link">
                  <ExternalLink size={14} /> {artForm.drive_link}
                </a>
              </div>
            )}
          </div>
          {artEditId && (
            <div className="border-t border-slate-100 pt-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Google Drive Ordner &amp; Kommentare</span>
                <div className="flex gap-2">
                  {!canDrive ? null : !artForm.drive_link ? (
                    <Button size="sm" onClick={ensureFolder} disabled={!drive.connected || folderBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="article-drive-folder-btn"><FolderPlus size={14} className="mr-1" /> {folderBusy ? "Ordner wird erstellt …" : <>Ordner erstellen &amp; verknüpfen</>}</Button>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => window.open(artForm.drive_link, "_blank")} data-testid="article-drive-open-btn"><ExternalLink size={14} className="mr-1" /> Ordner öffnen</Button>
                      <Button size="sm" variant="ghost" onClick={ensureFolder} title="Verknüpfung prüfen / erneuern" data-testid="article-drive-sync-btn"><RefreshCw size={14} /></Button>
                    </>
                  )}
                  {canDrive && artForm.upload_link && <Button size="sm" variant="outline" onClick={() => window.open(artForm.upload_link, "_blank")} title="Dateien hochladen – ohne Account" data-testid="article-upload-link-btn"><Upload size={14} className="mr-1" /> Hochladen</Button>}
                </div>
              </div>
              {canDrive && <p className="text-[11px] text-slate-400 mt-1">Der Ordner wird automatisch im richtigen Ausgaben-/Kategorie-Pfad angelegt und geteilt. <b>Ordner öffnen</b> = ansehen &amp; korrekturlesen (kommentieren/bearbeiten).</p>}
              {!drive.connected && <p className="text-xs text-amber-600 mt-2">Kein Cloud-Speicher verbunden (Einstellungen → Cloud-Speicher).</p>}

              <div className="mt-3">
                <div className="flex gap-2">
                  <Input value={newComment} onChange={(e) => setNewComment(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addComment(); }} placeholder="Kommentar hinzufügen…" className="h-9" data-testid="comment-input" />
                  <Button size="sm" onClick={addComment} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="comment-add-btn"><MessageSquare size={14} className="mr-1" /> Senden</Button>
                </div>
                <div className="mt-2 space-y-1.5 max-h-44 overflow-auto" data-testid="article-comments-list">
                  {commentsLoading && <p className="text-xs text-slate-400">Lädt…</p>}
                  {!commentsLoading && comments.comments.length === 0 && <p className="text-xs text-slate-400 italic">Noch keine Kommentare.</p>}
                  {comments.comments.map((c) => (
                    <div key={c.id} className={`text-xs border rounded-md p-2 ${c.resolved ? "bg-slate-50 border-slate-100 opacity-60" : "bg-amber-50/60 border-amber-100"}`}>
                      <div className="flex justify-between gap-2">
                        <span className="font-semibold text-slate-700">{c.author || "Unbekannt"}</span>
                        <span className="flex items-center gap-2">
                          <button onClick={() => resolveComment(c)} className="text-slate-400 hover:text-emerald-600" title={c.resolved ? "Wieder öffnen" : "Als erledigt markieren"} data-testid={`comment-resolve-${c.id}`}>{c.resolved ? "↺" : "✓"}</button>
                          <button onClick={() => deleteComment(c)} className="text-slate-400 hover:text-red-500" title="Löschen" data-testid={`comment-delete-${c.id}`}><Trash2 size={12} /></button>
                        </span>
                      </div>
                      <div className="text-slate-600 mt-0.5 whitespace-pre-wrap">{c.content}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <p className="text-xs text-slate-400">Status-Labels setzt du direkt auf der Karte per Klick.</p>
          <DialogFooter className="sm:justify-between">
            {artEditId && canDelete ? (
              <Button variant="outline" onClick={deleteArticleFromDialog} className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid="article-delete-button"><Trash2 size={14} className="mr-1.5" /> Artikel löschen</Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setArtOpen(false)}>Abbrechen</Button>
              {canArt && <Button onClick={saveArticle} disabled={artSaving} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="article-save-button">{artSaving ? "Speichern …" : "Speichern"}</Button>}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category dialog */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader><DialogTitle>Neue Kategorie</DialogTitle></DialogHeader>
          <div>
            <Label>Name</Label>
            <Input value={catName} onChange={(e) => setCatName(e.target.value)} className="mt-1" placeholder="z.B. 04 - Reportage" data-testid="category-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatOpen(false)}>Abbrechen</Button>
            <Button onClick={addCategory} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="category-save-button">Anlegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue settings dialog */}
      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="issue-dialog">
          <DialogHeader><DialogTitle>Ausgabe bearbeiten</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={issueForm.name || ""} onChange={(e) => setIssueForm({ ...issueForm, name: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Leitung</Label><Input value={issueForm.team_leitung || ""} onChange={(e) => setIssueForm({ ...issueForm, team_leitung: e.target.value })} className="mt-1" /></div>
              <div><Label>Layout</Label><Input value={issueForm.team_layout || ""} onChange={(e) => setIssueForm({ ...issueForm, team_layout: e.target.value })} className="mt-1" /></div>
              <div><Label>Redaktion</Label><Input value={issueForm.team_redaktion || ""} onChange={(e) => setIssueForm({ ...issueForm, team_redaktion: e.target.value })} className="mt-1" /></div>
              <div><Label>Kontakt</Label><Input value={issueForm.kontakt || ""} onChange={(e) => setIssueForm({ ...issueForm, kontakt: e.target.value })} className="mt-1" /></div>
            </div>
            <div>
              <Label>Redaktionsschluss</Label>
              <Input type="date" value={issueForm.redaktionsschluss || ""} onChange={(e) => setIssueForm({ ...issueForm, redaktionsschluss: e.target.value })} className="mt-1" data-testid="issue-redaktionsschluss" />
              <p className="text-xs text-slate-400 mt-1">Alle weiteren Termine werden automatisch aus der Zeitplan-Vorlage (Einstellungen → Team &amp; Zeitplan) berechnet und im Zeitplan der Ausgabe angezeigt.</p>
            </div>
          </div>
          <DialogFooter className="sm:justify-between gap-2">
            <Button variant="outline" onClick={toggleArchive} data-testid="issue-archive-button">
              {issue?.status === "archiv" ? <><ArchiveRestore size={15} className="mr-1.5" /> Wiederherstellen</> : <><Archive size={15} className="mr-1.5" /> Ins Archiv</>}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIssueOpen(false)}>Abbrechen</Button>
              <Button onClick={saveIssue} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="issue-save-button">Speichern</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
